import { useState, useEffect } from "react";
import {
  Box, Card, CardContent, CardHeader, Typography, FormControlLabel,
  FormGroup, Switch, Divider, Chip, Button, Avatar, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip,
  Badge, alpha, createTheme, ThemeProvider, CssBaseline, RadioGroup,
  Radio, FormControl, FormLabel, Autocomplete, TextField, Stack, Alert,
  AlertTitle, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Snackbar, CircularProgress, InputAdornment,
  Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import {
  CreditCardOutlined, SmsOutlined, AutorenewOutlined, SettingsOutlined,
  CheckCircleOutline, CancelOutlined, InfoOutlined, NotificationsActiveOutlined,
  LockOpenOutlined, VerifiedUserOutlined, HourglassTopOutlined,
  Add as AddIcon, Delete as DeleteIcon,
  DragIndicator as DragIcon, LocalHospital as InsIcon,
  CreditCard as IdCardIcon, SaveOutlined as SaveIcon,
  PrintOutlined as PrinterIcon,
  ExpandMore as ExpandMoreIcon,
  CloudDownloadOutlined as BackupIcon,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import axios from "axios";
import {
  getAllInsurancePartners, addInsurancePartner,
  updateInsurancePartner, deleteInsurancePartner,
  getIdCardAmount, saveIdCardAmount,
  getCardPrinterType, saveCardPrinterType,
  complianceSettings,
  smsSettings,
  downloadDatabaseBackup,
} from "../BaseURL";
import SmsSettings from "./SmsSettings";

// Theme
const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#1a6b8a" },
    secondary: { main: "#e07b39" },
    background: { default: "#f0f4f8", paper: "#ffffff" },
    success: { main: "#2e7d32" },
    warning: { main: "#ed6c02" },
    error: { main: "#c62828" },
  },
  typography: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    h5: { fontWeight: 700, letterSpacing: "-0.5px" },
    h6: { fontWeight: 700, fontSize: "1rem" },
    subtitle2: { fontWeight: 600 },
    caption: { fontSize: "0.72rem" },
  },
  shape: { borderRadius: 14 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 2px 16px 0 rgba(26,107,138,0.08)",
          border: "1px solid #e3eaf0",
          transition: "box-shadow 0.2s",
          "&:hover": { boxShadow: "0 6px 28px 0 rgba(26,107,138,0.13)" },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { paddingBottom: 8 },
        title: { fontWeight: 700, fontSize: "0.97rem" },
        subheader: { fontSize: "0.78rem", marginTop: 2 },
      },
    },
    MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
    MuiSwitch: { defaultProps: { color: "primary" } },
  },
});

const PATIENTS = [
  { id: "P001", name: "Kwame Asante",  dob: "1978-03-12" },
  { id: "P002", name: "Abena Mensah",  dob: "1990-07-25" },
  { id: "P003", name: "Kofi Boateng",  dob: "1965-11-04" },
  { id: "P004", name: "Ama Owusu",     dob: "2001-01-17" },
  { id: "P005", name: "Yaw Darko",     dob: "1985-09-30" },
  { id: "P006", name: "Efua Tetteh",   dob: "1972-06-08" },
];

const CREDIT_REQUESTS = [
  { id: "REQ001", patientId: "P009", name: "Samuel Nkrumah",  type: "Outpatient", amount: "GHS 450.00",   requestedBy: "Billing Desk", date: "2025-02-24" },
  { id: "REQ002", patientId: "P017", name: "Grace Amoah",     type: "Inpatient",  amount: "GHS 1,200.00", requestedBy: "Ward 3 Nurse", date: "2025-02-25" },
  { id: "REQ003", patientId: "P031", name: "Emmanuel Ofori",  type: "Outpatient", amount: "GHS 85.00",    requestedBy: "Billing Desk", date: "2025-02-25" },
];

function IconBox({ icon, color }) {
  return (
    <Box sx={{ width: 38, height: 38, borderRadius: 2.5, flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      bgcolor: alpha(color, 0.12), color }}>
      {icon}
    </Box>
  );
}

// Brand accent for the outer page — used for the header avatar tile.
const SETTINGS_ACCENT = "#1a6b8a";

/**
 * SettingsAccordion — uniform collapsible shell for every settings panel.
 * Wraps a legacy <Card>-based section and flattens the inner card so the
 * accordion feels like a single surface. Lets us reorganise the page into
 * grouped, expandable sections without touching each section's internals.
 */
function SettingsAccordion({ icon, color = SETTINGS_ACCENT, title, subtitle, defaultExpanded, children }) {
  return (
    <Accordion
      defaultExpanded={!!defaultExpanded}
      disableGutters
      square={false}
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        overflow: "hidden",
        "&:before": { display: "none" },
        "&.Mui-expanded": { borderColor: alpha(color, 0.5) },
        // Flatten inner Card so the accordion looks like one panel.
        "& .MuiCard-root": {
          boxShadow: "none",
          border: "none",
          bgcolor: "transparent",
          backgroundImage: "none",
        },
        "& .MuiCard-root .MuiCardHeader-root": { display: "none" },
        "& .MuiCard-root .MuiDivider-root:first-of-type": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
        sx={{
          px: 2, minHeight: 64,
          "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1.5, my: 1 },
          "&.Mui-expanded": { bgcolor: alpha(color, 0.08) },
          "&:hover":        { bgcolor: alpha(color, 0.04) },
        }}
      >
        <Box sx={{
          width: 36, height: 36, borderRadius: 1.75, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: alpha(color, 0.18), color,
          border: `1px solid ${alpha(color, 0.30)}`,
        }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: "0.76rem", color: "text.secondary", mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 1.5, md: 2 }, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

// 1. Credit & Billing
function CreditBillingSettings() {
  const [creditMode, setCreditMode] = useState("none");
  const [preApproved, setPreApproved] = useState([PATIENTS[0]]);
  const [saved, setSaved] = useState(false);
  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<CreditCardOutlined fontSize="small" />} color="#1a6b8a" />}
        title="Credit & Billing Permissions"
        subheader="Control which patient bills are eligible for crediting without approval"
        action={<Chip size="small" variant="outlined"
          label={creditMode === "all" ? "All Enabled" : creditMode === "none" ? "Disabled" : "Partial"}
          color={creditMode === "all" ? "success" : creditMode === "none" ? "default" : "warning"} />}
      />
      <Divider />
      <CardContent>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ fontWeight: 700, fontSize: "0.82rem", color: "text.primary", mb: 1 }}>
            Automatic Credit Authorization
          </FormLabel>
          <RadioGroup value={creditMode} onChange={(e) => setCreditMode(e.target.value)}>
            {[
              { value: "all",        label: "All patients bills can be credited",   desc: "Any bill in the system will process credit automatically.",   color: "success.main" },
              { value: "inpatient",  label: "Only inpatient bills can be credited",  desc: "Credit applies exclusively to admitted/ward patients.",       color: "primary.main" },
              { value: "outpatient", label: "Only outpatient bills can be credited", desc: "Credit applies exclusively to OPD/clinic visits.",           color: "warning.main" },
              { value: "none",       label: "No automatic crediting",                desc: "All credit requests require manual administrative approval.", color: "error.main"   },
            ].map((opt) => (
              <Box key={opt.value} onClick={() => setCreditMode(opt.value)} sx={{
                display: "flex", alignItems: "flex-start", p: 1.2, mb: 1, borderRadius: 2,
                border: "1.5px solid", cursor: "pointer", transition: "all 0.18s",
                borderColor: creditMode === opt.value ? opt.color : "divider",
                bgcolor: creditMode === opt.value ? alpha("#1a6b8a", 0.04) : "transparent",
              }}>
                <Radio value={opt.value} size="small" sx={{ mt: -0.3, color: opt.color }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontSize: "0.85rem" }}>{opt.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{opt.desc}</Typography>
                </Box>
              </Box>
            ))}
          </RadioGroup>
        </FormControl>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1, fontSize: "0.82rem", color: "text.primary" }}>
          Pre-Approved Patients{" "}
          <Typography component="span" variant="caption" color="text.secondary">
            — always credited regardless of setting above
          </Typography>
        </Typography>
        <Autocomplete multiple options={PATIENTS} getOptionLabel={(o) => `${o.name} (${o.id})`}
          value={preApproved} onChange={(_, v) => setPreApproved(v)}
          renderInput={(params) => <TextField {...params} size="small" placeholder="Search patient name or ID" />}
          renderTags={(val, getTagProps) =>
            val.map((opt, idx) => (
              <Chip key={opt.id} label={`${opt.name} - ${opt.id}`} size="small" color="primary" variant="outlined" {...getTagProps({ index: idx })} />
            ))}
          sx={{ mb: 2 }} />
        <Button variant="contained" size="small" disableElevation onClick={() => setSaved(true)} sx={{ borderRadius: 2 }}>
          Save Credit Settings
        </Button>
        <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)}
          message="Credit settings saved successfully" anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
      </CardContent>
    </Card>
  );
}

// 2. Credit Authorization Requests
function CreditAuthorizationRequests() {
  const [requests, setRequests] = useState(CREDIT_REQUESTS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReq, setSelectedReq] = useState(null);
  const [action, setAction] = useState("approve");
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const handleAction = (req, type) => { setSelectedReq(req); setAction(type); setDialogOpen(true); };
  const confirmAction = () => {
    setRequests((prev) => prev.filter((r) => r.id !== selectedReq.id));
    setDialogOpen(false);
    setSnack({ open: true, severity: action === "approve" ? "success" : "error",
      msg: action === "approve" ? `Credit approved for ${selectedReq.name}` : `Request declined for ${selectedReq.name}` });
  };
  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<LockOpenOutlined fontSize="small" />} color="#e07b39" />}
        title="Credit Authorization Requests"
        subheader="Pending billing-side requests requiring administrative approval"
        action={<Badge badgeContent={requests.length} color="error"><HourglassTopOutlined color="action" /></Badge>}
      />
      <Divider />
      <CardContent sx={{ p: 0 }}>
        {requests.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <VerifiedUserOutlined sx={{ fontSize: 40, color: "success.light", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">No pending authorization requests</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f7fafc" }}>
                  {["Patient", "ID", "Type", "Amount", "Requested By", "Date", "Actions"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.82rem" }}>{r.name}</TableCell>
                    <TableCell><Chip label={r.patientId} size="small" variant="outlined" /></TableCell>
                    <TableCell><Chip label={r.type} size="small" variant="filled" sx={{ fontSize: "0.72rem" }} color={r.type === "Inpatient" ? "primary" : "warning"} /></TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "error.main", fontSize: "0.82rem" }}>{r.amount}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: "text.secondary" }}>{r.requestedBy}</TableCell>
                    <TableCell sx={{ fontSize: "0.78rem", color: "text.secondary" }}>{r.date}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Approve Credit">
                          <IconButton size="small" color="success" onClick={() => handleAction(r, "approve")} sx={{ bgcolor: alpha("#2e7d32", 0.08) }}>
                            <CheckCircleOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Decline Request">
                          <IconButton size="small" color="error" onClick={() => handleAction(r, "decline")} sx={{ bgcolor: alpha("#c62828", 0.08) }}>
                            <CancelOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{action === "approve" ? "Approve Credit Request?" : "Decline Credit Request?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {action === "approve"
              ? `This will authorize the credit of ${selectedReq?.amount} for ${selectedReq?.name}. The system will process this automatically.`
              : `This will decline the credit request for ${selectedReq?.name}. The billing desk will be notified.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button onClick={() => setDialogOpen(false)} size="small">Cancel</Button>
          <Button variant="contained" size="small" disableElevation onClick={confirmAction}
            sx={{ borderRadius: 2 }} color={action === "approve" ? "success" : "error"}>
            {action === "approve" ? "Confirm Approval" : "Confirm Decline"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>{snack.msg}</Alert>
      </Snackbar>
    </Card>
  );
}

// 3. SMS Settings
// The master ON/OFF toggle is the only setting on this card that is
// persisted server-side (into system_settings.sms.enabled — same source
// SmsService reads at send time). The per-event switches below are UI
// placeholders — the corresponding automatic triggers don't yet exist
// in the backend (only the welcome-package one is wired via
// PatientInformation.js and always runs when the master toggle is ON
// and an API key is configured). The full provider config (API key,
// sender ID, endpoints, balance check, test send) lives on the
// dedicated "SMS Settings" page.
function SmsAlertSettings() {
  const [masterSms, setMasterSms] = useState(true);
  const [loadingMaster, setLoadingMaster] = useState(true);
  const [savingMaster,  setSavingMaster]  = useState(false);
  const [options, setOptions] = useState({ welcomePackage: true, onApproval: true, onBillGenerated: false, onDischarge: true, onAppointment: false });
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const toggle = (key) => setOptions((o) => ({ ...o, [key]: !o[key] }));

  // Load the persisted master flag on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(smsSettings);
        if (!cancelled && res.data?.success) {
          setMasterSms(!!res.data.data.enabled);
        }
      } catch (e) {
        console.error('SMS settings load failed:', e.message);
      } finally {
        if (!cancelled) setLoadingMaster(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onMasterChange = async (e) => {
    const next = e.target.checked;
    setMasterSms(next);   // optimistic
    setSavingMaster(true);
    try {
      // Only send `enabled` — leave the rest of the SMS config (api_key,
      // sender_id, paths) untouched by omitting them. The PUT handler
      // preserves any field it doesn't receive.
      const res = await axios.put(smsSettings, { enabled: next });
      if (!res.data?.success) throw new Error(res.data?.message || 'Save failed');
      setSnack({ open: true, msg: `SMS gateway ${next ? 'enabled' : 'disabled'}`, severity: 'success' });
    } catch (err) {
      setMasterSms(!next);  // roll back
      setSnack({ open: true, msg: `Failed to save: ${err.message}`, severity: 'error' });
    } finally {
      setSavingMaster(false);
    }
  };

  const smsOptions = [
    { key: "welcomePackage",  label: "Welcome package SMS",       desc: "Sent to new patients upon registration (wired)" },
    { key: "onApproval",      label: "Credit approval SMS",        desc: "Notify patient when credit is approved (not wired yet)" },
    { key: "onBillGenerated", label: "Bill generated SMS",         desc: "Alert patient when a new bill is raised (not wired yet)" },
    { key: "onDischarge",     label: "Discharge notification SMS", desc: "Send summary on patient discharge (not wired yet)" },
    { key: "onAppointment",   label: "Appointment reminder SMS",   desc: "Remind patients of upcoming appointments (sent immediately, not scheduled)" },
  ];
  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<SmsOutlined fontSize="small" />} color="#7b3f9e" />}
        title="SMS Alert Settings"
        subheader="Master gateway on/off. Provider credentials live on the SMS Settings page."
        action={
          <FormControlLabel labelPlacement="start"
            control={
              <Switch
                checked={masterSms}
                onChange={onMasterChange}
                size="small"
                disabled={loadingMaster || savingMaster}
              />
            }
            label={
              <Typography variant="caption" fontWeight={700} color={masterSms ? "success.main" : "text.disabled"}>
                {loadingMaster ? '…' : (masterSms ? "SMS ON" : "SMS OFF")}
              </Typography>
            }
          />}
      />
      <Divider />
      <CardContent>
        {!masterSms && !loadingMaster && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            <AlertTitle sx={{ fontSize: "0.82rem", fontWeight: 700 }}>SMS globally disabled</AlertTitle>
            No SMS will be sent regardless of individual settings below.
          </Alert>
        )}
        <FormGroup>
          {smsOptions.map((opt) => (
            <Box key={opt.key} sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              py: 1.2, px: 1.5, mb: 0.8, borderRadius: 2, transition: "all 0.18s",
              bgcolor: options[opt.key] && masterSms ? alpha("#7b3f9e", 0.05) : "#f8f8f8",
              border: "1px solid", borderColor: options[opt.key] && masterSms ? alpha("#7b3f9e", 0.2) : "divider",
              opacity: masterSms ? 1 : 0.5,
            }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontSize: "0.84rem" }}>{opt.label}</Typography>
                <Typography variant="caption" color="text.secondary">{opt.desc}</Typography>
              </Box>
              <Switch checked={options[opt.key]} onChange={() => toggle(opt.key)} size="small" disabled={!masterSms} />
            </Box>
          ))}
        </FormGroup>
        <Snackbar
          open={snack.open}
          autoHideDuration={2500}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>{snack.msg}</Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
}

// 4. Auto-Update
function AutoUpdateSettings() {
  const [opts, setOpts] = useState({ masterAutoUpdate: true, billsAutoRefresh: true, stockAutoSync: false, labResultsAutoLoad: true, reportsAutoGenerate: false });
  const [saved, setSaved] = useState(false);
  const toggle = (k) => setOpts((o) => ({ ...o, [k]: !o[k] }));
  const items = [
    { key: "billsAutoRefresh",    label: "Bills auto-refresh",        desc: "Automatically refresh billing lists in real-time" },
    { key: "stockAutoSync",       label: "Stock/pharmacy auto-sync",   desc: "Sync pharmacy and store stock levels automatically" },
    { key: "labResultsAutoLoad",  label: "Lab results auto-load",      desc: "Push lab results to patient records automatically" },
    { key: "reportsAutoGenerate", label: "Reports auto-generate",      desc: "Auto-generate daily/weekly reports on schedule" },
  ];
  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<AutorenewOutlined fontSize="small" />} color="#2e8b57" />}
        title="Auto-Update Settings"
        subheader="Control system-wide automatic data refresh and sync behaviour"
        action={
          <FormControlLabel labelPlacement="start"
            control={<Switch checked={opts.masterAutoUpdate} onChange={() => toggle("masterAutoUpdate")} size="small" />}
            label={<Typography variant="caption" fontWeight={700} color={opts.masterAutoUpdate ? "success.main" : "text.disabled"}>{opts.masterAutoUpdate ? "AUTO ON" : "AUTO OFF"}</Typography>}
          />}
      />
      <Divider />
      <CardContent>
        <FormGroup>
          {items.map((item) => (
            <Box key={item.key} sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              py: 1.2, px: 1.5, mb: 0.8, borderRadius: 2, transition: "all 0.18s",
              bgcolor: opts[item.key] && opts.masterAutoUpdate ? alpha("#2e8b57", 0.05) : "#f8f8f8",
              border: "1px solid", borderColor: opts[item.key] && opts.masterAutoUpdate ? alpha("#2e8b57", 0.2) : "divider",
              opacity: opts.masterAutoUpdate ? 1 : 0.5,
            }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontSize: "0.84rem" }}>{item.label}</Typography>
                <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
              </Box>
              <Switch checked={opts[item.key]} onChange={() => toggle(item.key)} size="small" disabled={!opts.masterAutoUpdate} />
            </Box>
          ))}
        </FormGroup>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" size="small" disableElevation onClick={() => setSaved(true)}
            sx={{ borderRadius: 2, bgcolor: "#2e8b57", "&:hover": { bgcolor: "#1e6b40" } }}>
            Save Auto-Update Settings
          </Button>
        </Box>
        <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)} message="Auto-update settings saved" anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
      </CardContent>
    </Card>
  );
}

// 5. Notifications
function NotificationSettings() {
  const [opts, setOpts] = useState({ dashboardAlerts: true, emailSummary: false, criticalAlerts: true, auditLog: true });
  const [saved, setSaved] = useState(false);
  const toggle = (k) => setOpts((o) => ({ ...o, [k]: !o[k] }));
  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<NotificationsActiveOutlined fontSize="small" />} color="#c0392b" />}
        title="Notification & Alert Preferences"
        subheader="System notifications for administrators and authorised staff"
      />
      <Divider />
      <CardContent>
        <FormGroup>
          {[
            { key: "dashboardAlerts", label: "Dashboard alert banners", desc: "Show system alerts on the admin dashboard" },
            { key: "emailSummary",    label: "Daily email summary",      desc: "Send a daily digest email to the admin account" },
            { key: "criticalAlerts",  label: "Critical event alerts",    desc: "Immediate alerts for critical billing or system events" },
            { key: "auditLog",        label: "Audit log recording",      desc: "Log all administrative actions for compliance review" },
          ].map((item) => (
            <Box key={item.key} sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              py: 1.2, px: 1.5, mb: 0.8, borderRadius: 2, transition: "all 0.18s",
              bgcolor: opts[item.key] ? alpha("#c0392b", 0.04) : "#f8f8f8",
              border: "1px solid", borderColor: opts[item.key] ? alpha("#c0392b", 0.18) : "divider",
            }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontSize: "0.84rem" }}>{item.label}</Typography>
                <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
              </Box>
              <Switch checked={opts[item.key]} onChange={() => toggle(item.key)} size="small" />
            </Box>
          ))}
        </FormGroup>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" size="small" disableElevation onClick={() => setSaved(true)}
            sx={{ borderRadius: 2, bgcolor: "#c0392b", "&:hover": { bgcolor: "#922b21" } }}>
            Save Notification Settings
          </Button>
        </Box>
        <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)} message="Notification settings saved" anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
      </CardContent>
    </Card>
  );
}

// 6. Insurance Partners (integrated from InsurancePartnerSettings.jsx)
function InsurancePartnerSettings() {
  const [partners, setPartners] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [newName,  setNewName]  = useState("");
  const [newShort, setNewShort] = useState("");
  const [adding,   setAdding]   = useState(false);

  const user     = JSON.parse(localStorage.getItem("user") || "{}");
  const username = user?.username || user?.name || "admin";

  const loadPartners = () => {
    setLoading(true);
    fetch(getAllInsurancePartners)
      .then((r) => r.json())
      .then((data) => { if (data?.success) setPartners(data.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadPartners(); }, []);

  const activeCount = partners.filter((p) => p.is_active).length;

  const handleAdd = () => {
    if (!newName.trim()) return;
    setAdding(true);
    fetch(addInsurancePartner, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newName.trim(), short_name: newShort.trim() || null, is_active: 1, sort_order: partners.length, created_by: username }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.success) { setNewName(""); setNewShort(""); loadPartners(); }
        else Swal.fire("Error", data.message || "Failed to add", "error");
      })
      .catch(() => Swal.fire("Error", "Network error", "error"))
      .finally(() => setAdding(false));
  };

  const handleToggle = (partner) => {
    const newActive = partner.is_active ? 0 : 1;
    if (newActive === 1 && activeCount >= 10) {
      Swal.fire("Limit Reached", "Only 10 partners can be active on the card at once. Deactivate one first.", "warning");
      return;
    }
    fetch(updateInsurancePartner, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...partner, is_active: newActive }),
    })
      .then((r) => r.json())
      .then((data) => { if (data?.success) loadPartners(); else Swal.fire("Error", data.message, "error"); })
      .catch(() => {});
  };

  const handleDelete = (partner) => {
    Swal.fire({ title: "Delete partner?", text: `Remove "${partner.name}" from the list?`, icon: "warning",
      showCancelButton: true, confirmButtonColor: "#d33", confirmButtonText: "Yes, delete" })
      .then((result) => {
        if (!result.isConfirmed) return;
        fetch(deleteInsurancePartner, {
          method: "DELETE", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: partner.id }),
        })
          .then((r) => r.json())
          .then((data) => { if (data?.success) loadPartners(); else Swal.fire("Error", data.message, "error"); })
          .catch(() => {});
      });
  };

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<InsIcon fontSize="small" />} color="#1a6b8a" />}
        title="Insurance Partners"
        subheader="Manage partners displayed on the patient ID card (up to 10 active)"
        action={
          <Chip size="small" variant="outlined"
            label={`${activeCount} / 10 active`}
            color={activeCount >= 10 ? "warning" : "success"} />
        }
      />
      <Divider />
      <CardContent>
        <Box sx={{ display: "flex", gap: 1, mb: 2.5, alignItems: "flex-start" }}>
          <TextField size="small" label="Partner name" value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            sx={{ flex: 2 }} />
          <TextField size="small" label="Short name" value={newShort}
            onChange={(e) => setNewShort(e.target.value)}
            sx={{ flex: 1 }} inputProps={{ maxLength: 10 }} />
          <Button variant="contained" size="small" disableElevation onClick={handleAdd}
            disabled={adding || !newName.trim()}
            startIcon={adding ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
            sx={{ height: 40, whiteSpace: "nowrap", borderRadius: 2 }}>
            Add
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : partners.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: "center", fontSize: "0.85rem" }}>
            No insurance partners yet. Add one above.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
            {partners.map((p) => (
              <Box key={p.id} sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                px: 1.5, py: 1, borderRadius: 2, transition: "all 0.2s",
                background: p.is_active ? alpha("#1a6b8a", 0.04) : "#fafafa",
                border: "1px solid", borderColor: p.is_active ? alpha("#1a6b8a", 0.15) : "divider",
                opacity: p.is_active ? 1 : 0.6,
              }}>
                <DragIcon sx={{ color: "#ccc", fontSize: 18, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600} fontSize={13} noWrap>{p.name}</Typography>
                  {p.short_name && <Typography variant="caption" color="text.secondary">{p.short_name}</Typography>}
                </Box>
                <Chip size="small" variant="outlined" sx={{ fontSize: "0.7rem", mr: 0.5 }}
                  label={p.is_active ? "Active" : "Hidden"}
                  color={p.is_active ? "success" : "default"} />
                <Tooltip title={p.is_active ? "Deactivate" : "Activate"}>
                  <Switch size="small" checked={Boolean(p.is_active)} onChange={() => handleToggle(p)} />
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => handleDelete(p)} sx={{ color: "error.light" }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
          Toggle the switch to show or hide a partner on the ID card. Only the first 10 active partners are printed.
        </Typography>
      </CardContent>
    </Card>
  );
}


// 7. ID Card Amount Setup
function IdCardAmountSettings() {
  const [amount,  setAmount]  = useState("");
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState("");

  const user     = JSON.parse(localStorage.getItem("user") || "{}");
  const username = user?.username || user?.name || "admin";

  useEffect(() => {
    fetch(getIdCardAmount)
      .then((r) => r.json())
      .then((data) => {
        if (data?.success && data?.data?.amount != null)
          setAmount(String(data.data.amount));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = () => {
    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed < 0) {
      setError("Please enter a valid amount (0 or greater).");
      return;
    }
    setError("");
    setSaving(true);
    fetch(saveIdCardAmount, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ amount: parsed, updated_by: username }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.success) setSaved(true);
        else setError(data?.message || "Failed to save.");
      })
      .catch(() => setError("Network error. Please try again."))
      .finally(() => setSaving(false));
  };

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<IdCardIcon fontSize="small" />} color="#0277bd" />}
        title="ID Card Amount Setup"
        subheader="Set the fee charged to patients for printing an ID card"
        action={
          <Chip size="small" variant="outlined" color="info"
            label={loading ? "Loading..." : amount !== "" ? ("GHS " + parseFloat(amount || 0).toFixed(2)) : "Not set"} />
        }
      />
      <Divider />
      <CardContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              This amount is charged whenever a patient ID card is printed or re-printed.
              Set to <strong>0</strong> to make ID cards free of charge.
            </Typography>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="ID Card Fee"
                type="number"
                value={amount}
                onChange={(e) => { setAmount(e.target.value); setError(""); }}
                onKeyDown={(e) => e.key === "Enter" && handleSave()}
                size="small"
                sx={{ width: 220 }}
                inputProps={{ min: 0, step: "0.01" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body2" fontWeight={700} color="text.secondary">GHS</Typography>
                    </InputAdornment>
                  ),
                }}
                error={Boolean(error)}
                helperText={error || " "}
              />
              <Button
                variant="contained" size="small" disableElevation
                onClick={handleSave}
                disabled={saving || amount === ""}
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                sx={{ height: 40, borderRadius: 2, bgcolor: "#0277bd", "&:hover": { bgcolor: "#01579b" } }}
              >
                {saving ? "Saving..." : "Save Amount"}
              </Button>
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 600 }}>
                Quick presets:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {["0", "2.00", "5.00", "10.00", "20.00", "50.00"].map((preset) => (
                  <Chip
                    key={preset}
                    label={preset === "0" ? "Free" : ("GHS " + preset)}
                    size="small"
                    variant={amount === preset ? "filled" : "outlined"}
                    color={amount === preset ? "info" : "default"}
                    onClick={() => { setAmount(preset); setError(""); }}
                    sx={{ cursor: "pointer", fontWeight: 600, fontSize: "0.72rem" }}
                  />
                ))}
              </Stack>
            </Box>
          </>
        )}
      </CardContent>

      <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>
          ID card fee updated successfully
        </Alert>
      </Snackbar>
    </Card>
  );
}

// 7b. ID Card Printer Type
// Facilities standardised on either the Magicard 300 or the Zebra ZXP
// Series 3C — the two printers use different card-print pipelines and
// slightly different safe-margins, so the OPD print button reads this
// setting at print time and picks the matching HTML layout.
function CardPrinterSettings() {
  const PRINTERS = [
    {
      value: "magicard_300",
      label: "Magicard 300",
      desc: "Direct-to-card, YMCKO ribbon. Uses the full CR80 print area (85.6 × 54 mm bleed).",
    },
    {
      value: "zebra_zxp_3c",
      label: "Zebra ZXP Series 3C",
      desc: "YMCKO print, 1 mm no-print zone at the card edges. Layout is inset for edge-safe printing.",
    },
  ];

  const [printer, setPrinter] = useState("magicard_300");
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState("");

  const user     = JSON.parse(localStorage.getItem("user") || "{}");
  const username = user?.username || user?.name || "admin";

  useEffect(() => {
    fetch(getCardPrinterType)
      .then((r) => r.json())
      .then((data) => {
        const t = data?.data?.printer_type;
        if (t && PRINTERS.some((p) => p.value === t)) setPrinter(t);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = () => {
    setError("");
    setSaving(true);
    fetch(saveCardPrinterType, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ printer_type: printer, updated_by: username }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.success) setSaved(true);
        else setError(data?.message || "Failed to save.");
      })
      .catch(() => setError("Network error. Please try again."))
      .finally(() => setSaving(false));
  };

  const currentLabel = PRINTERS.find((p) => p.value === printer)?.label || "—";

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<PrinterIcon fontSize="small" />} color="#00695c" />}
        title="ID Card Printer"
        subheader="Pick the physical printer this facility uses so the OPD print job matches its layout"
        action={
          <Chip size="small" variant="outlined" color="success"
            label={loading ? "Loading…" : currentLabel} />
        }
      />
      <Divider />
      <CardContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The OPD "Print ID Card" button will automatically use this printer's layout.
              Switching printers does not require the front-office to change anything else.
            </Typography>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup value={printer} onChange={(e) => setPrinter(e.target.value)}>
                {PRINTERS.map((p) => (
                  <FormControlLabel
                    key={p.value}
                    value={p.value}
                    control={<Radio />}
                    sx={{
                      alignItems: "flex-start", mb: 1, mr: 0,
                      "& .MuiFormControlLabel-label": { width: "100%" },
                    }}
                    label={
                      <Box sx={{ pt: 0.25 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>
                          {p.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {p.desc}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>

            {error && (
              <Alert severity="error" variant="outlined" sx={{ mt: 1.5 }}>{error}</Alert>
            )}

            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained" size="small" disableElevation
                onClick={handleSave} disabled={saving}
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                sx={{ height: 40, borderRadius: 2, bgcolor: "#00695c",
                      "&:hover": { bgcolor: "#004d40" } }}
              >
                {saving ? "Saving…" : "Save Printer"}
              </Button>
            </Box>
          </>
        )}
      </CardContent>

      <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>
          Card printer preference updated
        </Alert>
      </Snackbar>
    </Card>
  );
}

// 8. General placeholder
function GeneralSettings() {
  return (
    <Card sx={{ borderStyle: "dashed", bgcolor: "#fafcfe" }}>
      <CardHeader
        avatar={<IconBox icon={<SettingsOutlined fontSize="small" />} color="#888" />}
        title="Additional Settings"
        subheader="Reserved for future configuration modules"
      />
      <Divider />
      <CardContent>
        <Box sx={{ minHeight: 120, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "text.disabled", gap: 1 }}>
          <InfoOutlined sx={{ fontSize: 36 }} />
          <Typography variant="body2">More settings will appear here</Typography>
          <Typography variant="caption">Contact your system administrator to configure additional modules.</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

// ROOT
// Compliance — Vitals & Nursing Notes per-day rule
function ComplianceSettings() {
  const [startDate, setStartDate]   = useState("");
  const [draftDate, setDraftDate]   = useState("");
  const [minPerDay, setMinPerDay]   = useState(3);
  const [enabled, setEnabled]       = useState("on");      // server value
  const [draftEnabled, setDraftEnabled] = useState("on");  // toggle in UI
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [pwdOpen, setPwdOpen]       = useState(false);
  const [pwdMode, setPwdMode]       = useState("date");    // 'date' | 'toggle'
  const [username, setUsername]     = useState("");
  const [password, setPassword]     = useState("");
  const [error, setError]           = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(complianceSettings);
      if (data?.success) {
        setStartDate(data.startDate || "");
        setDraftDate(data.startDate || "");
        setMinPerDay(data.minPerDay || 3);
        const en = (data.enabled || "on") === "off" ? "off" : "on";
        setEnabled(en);
        setDraftEnabled(en);
      }
    } catch (err) {
      console.error("Failed to load compliance settings:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSaveClick = () => {
    setError("");
    if (!draftDate) { setError("Please pick a start date."); return; }
    if (draftDate === startDate) { setError("No changes to save."); return; }
    setPwdMode("date");
    setUsername("");
    setPassword("");
    setPwdOpen(true);
  };

  // Triggered when the admin flips the Switch. Confirms with password before
  // sending the change — same admin gate the start-date update already uses.
  const onToggleEnabled = (e) => {
    const next = e.target.checked ? "on" : "off";
    setDraftEnabled(next);
    setError("");
    setPwdMode("toggle");
    setUsername("");
    setPassword("");
    setPwdOpen(true);
  };

  const submitUpdate = async () => {
    if (!username.trim() || !password) {
      setError("Username and password are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        username: username.trim(),
        password,
      };
      if (pwdMode === "date") payload.startDate = draftDate;
      else                    payload.enabled   = draftEnabled;

      const { data } = await axios.post(complianceSettings, payload);
      if (data?.success) {
        setStartDate(data.startDate || "");
        setDraftDate(data.startDate || "");
        const en = (data.enabled || "on") === "off" ? "off" : "on";
        setEnabled(en);
        setDraftEnabled(en);
        setPwdOpen(false);
        Swal.fire({
          icon: "success",
          title: pwdMode === "toggle"
            ? (en === "on" ? "Compliance rule turned ON" : "Compliance rule turned OFF")
            : "Compliance start date updated",
          text: pwdMode === "toggle"
            ? (en === "on"
                ? "Discharge will now be blocked when the per-day rule is not met."
                : "Discharge can now proceed without meeting the per-day rule.")
            : `Effective from ${data.startDate}.`,
          timer: 2400,
          showConfirmButton: false,
        });
      } else {
        setError(data?.message || "Update failed.");
        if (pwdMode === "toggle") setDraftEnabled(enabled); // rollback
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.message || "Update failed.";
      if (status === 401) setError("Incorrect username or password.");
      else if (status === 403) setError("Only a System Administrator can update this setting.");
      else setError(msg);
      if (pwdMode === "toggle") setDraftEnabled(enabled); // rollback
    } finally {
      setSaving(false);
    }
  };

  const cancelPwd = () => {
    setPwdOpen(false);
    setError("");
    if (pwdMode === "toggle") setDraftEnabled(enabled); // rollback the Switch
  };

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<HourglassTopOutlined />} color="#b45309" />}
        title="Admission Compliance"
        subheader={`Minimum ${minPerDay} vitals & ${minPerDay} nursing notes per admission day`}
      />
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Admissions whose admission date is <strong>before</strong> the start date below are exempt
          and will not block discharge. Pick a future or current date to phase the rule in cleanly.
        </Typography>

        <Stack spacing={1.25}>
          {/* Master on/off — when OFF, the discharge gate is skipped entirely */}
          <Box
            sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              p: 1.25, borderRadius: 1.5,
              border: "1px solid",
              borderColor: draftEnabled === "on" ? "rgba(46,125,50,0.35)" : "rgba(198,40,40,0.35)",
              bgcolor:     draftEnabled === "on" ? "rgba(46,125,50,0.06)" : "rgba(198,40,40,0.06)",
            }}
          >
            <Box sx={{ minWidth: 0, pr: 1 }}>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "text.primary" }}>
                Enforce per-day vitals &amp; nursing notes
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {draftEnabled === "on"
                  ? `ON — discharge is blocked until each admission day has ≥${minPerDay} vitals and ≥1 nursing note.`
                  : "OFF — staff can discharge a patient regardless of per-day vitals/notes."}
              </Typography>
            </Box>
            <FormControlLabel
              sx={{ mr: 0 }}
              control={
                <Switch
                  checked={draftEnabled === "on"}
                  onChange={onToggleEnabled}
                  disabled={loading || saving}
                />
              }
              label=""
            />
          </Box>

          <TextField
            label="Compliance Start Date"
            type="date"
            value={draftDate}
            onChange={(e) => setDraftDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={loading || enabled === "off"}
            size="small"
            fullWidth
            helperText={enabled === "off"
              ? "Start date is ignored while the rule is OFF."
              : " "}
          />
          {startDate && (
            <Typography variant="caption" color="text.secondary">
              Currently effective from <strong>{startDate}</strong>.
            </Typography>
          )}
          {error && !pwdOpen && (
            <Alert severity="error" variant="outlined" sx={{ py: 0.25 }}>{error}</Alert>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 0.5 }}>
            <Button
              size="small"
              onClick={() => { setDraftDate(startDate); setError(""); }}
              disabled={loading || saving || draftDate === startDate}
            >
              Reset
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={onSaveClick}
              disabled={loading || saving || draftDate === startDate}
            >
              Update
            </Button>
          </Box>
        </Stack>
      </CardContent>

      <Dialog open={pwdOpen} onClose={() => !saving && cancelPwd()} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Confirm — System Administrator</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5, fontSize: '0.85rem' }}>
            {pwdMode === "toggle"
              ? <>You are about to turn the per-day vitals/notes rule <strong>{draftEnabled === "on" ? "ON" : "OFF"}</strong>. Enter your admin credentials to apply.</>
              : <>New start date: <strong>{draftDate}</strong>. Enter your admin credentials to apply.</>}
          </DialogContentText>
          <Stack spacing={1.25}>
            <TextField
              label="Username"
              size="small"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              size="small"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
            />
            {error && (
              <Alert severity="error" variant="outlined" sx={{ py: 0.25 }}>{error}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={cancelPwd} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={submitUpdate}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : null}
          >
            {saving ? "Saving…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

// Database Backup — admin-gated one-click download of the entire DB as
// a gzipped mysqldump. Streams straight from the backend so a 2 GB DB
// arrives as roughly a 200 MB .sql.gz file. Password confirmation reuses
// the same admin-credentials pattern the Compliance settings use.
function DatabaseBackupSettings() {
  const [pwdOpen, setPwdOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const openConfirm = () => {
    setUsername("");
    setPassword("");
    setError("");
    setPwdOpen(true);
  };

  const cancel = () => {
    if (downloading) return;
    setPwdOpen(false);
    setError("");
  };

  const runDownload = async () => {
    if (!username.trim() || !password) {
      setError("Username and password are required.");
      return;
    }
    setError("");
    setDownloading(true);
    try {
      // Long-running: expect up to a few minutes for a big DB. Using
      // fetch with responseType blob so we can trigger a browser download
      // without navigating away from the page.
      const response = await fetch(downloadDatabaseBackup, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim(), password }),
      });

      // Backend returns JSON on error (auth / mysqldump-missing) BEFORE
      // any data is streamed. Detect that by the Content-Type.
      const ct = response.headers.get("Content-Type") || "";
      if (!response.ok || ct.startsWith("application/json")) {
        const data = ct.startsWith("application/json") ? await response.json() : { message: `HTTP ${response.status}` };
        throw new Error(data?.message || "Backup failed.");
      }

      // Success — pull the whole blob and trigger a download.
      const blob = await response.blob();
      const disp = response.headers.get("Content-Disposition") || "";
      const match = /filename="?([^"]+)"?/i.exec(disp);
      const filename = match?.[1] || `database_backup_${Date.now()}.sql.gz`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setPwdOpen(false);
      Swal.fire({
        icon: "success",
        title: "Backup downloaded",
        text: `${filename} has been saved to your computer.`,
        timer: 3000,
        showConfirmButton: false,
      });
    } catch (err) {
      const msg = err?.message || "Backup failed.";
      setError(msg);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<BackupIcon fontSize="small" />} color="#0369a1" />}
        title="Database Backup"
        subheader="Download a full snapshot of the hospital database as a .sql.gz file"
      />
      <Divider />
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Generates a live snapshot of every table (patients, bills, labs, medications,
          audit logs — everything) and downloads it as a compressed SQL file. Use the
          resulting file with any MySQL/MariaDB tool to restore or migrate.
        </Typography>
        <Alert severity="info" variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
          <AlertTitle sx={{ fontSize: "0.82rem", fontWeight: 700 }}>What to expect</AlertTitle>
          Small databases finish in a few seconds. A 1–2 GB database can take
          several minutes to stream. Keep this tab open until the file save
          dialog appears in your browser.
        </Alert>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={<BackupIcon />}
            onClick={openConfirm}
            sx={{ height: 40, borderRadius: 2, bgcolor: "#0369a1", "&:hover": { bgcolor: "#075985" } }}
          >
            Download Database
          </Button>
        </Box>
      </CardContent>

      <Dialog open={pwdOpen} onClose={cancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Confirm — System Administrator</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5, fontSize: "0.85rem" }}>
            Downloading the full database is restricted to System Administrators.
            Enter your admin credentials to start the backup.
          </DialogContentText>
          <Stack spacing={1.25}>
            <TextField
              label="Username"
              size="small"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              fullWidth
              disabled={downloading}
            />
            <TextField
              label="Password"
              type="password"
              size="small"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              disabled={downloading}
            />
            {error && (
              <Alert severity="error" variant="outlined" sx={{ py: 0.25 }}>{error}</Alert>
            )}
            {downloading && (
              <Alert severity="info" variant="outlined" sx={{ py: 0.25 }}>
                Preparing the backup. Please don't close this tab — this can take a few minutes on larger databases.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={cancel} disabled={downloading}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={runDownload}
            disabled={downloading}
            startIcon={downloading ? <CircularProgress size={14} sx={{ color: "white" }} /> : <BackupIcon />}
          >
            {downloading ? "Downloading…" : "Start Backup"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default function HospitalAdminSettings() {
  // Sections grouped by area. Each item becomes a collapsible accordion;
  // add a new section by dropping a row into the right group. Keeping the
  // wiring here in one place means individual section components stay
  // untouched — they just render inside the accordion body.
  const SECTIONS = [
    {
      group: "Billing & Authorization",
      items: [
        { id: "credit",       icon: <CreditCardOutlined  fontSize="small" />, color: "#1a6b8a", title: "Credit & Billing Permissions",  subtitle: "Control which patient bills are eligible for crediting without approval", component: <CreditBillingSettings />,        defaultExpanded: true },
        { id: "auth",         icon: <LockOpenOutlined    fontSize="small" />, color: "#e07b39", title: "Credit Authorization Requests", subtitle: "Pending billing-side requests requiring administrative approval",         component: <CreditAuthorizationRequests /> },
        { id: "idcard",       icon: <IdCardIcon          fontSize="small" />, color: "#0277bd", title: "ID Card Amount Setup",          subtitle: "Set the fee charged to patients for printing an ID card",                 component: <IdCardAmountSettings /> },
      ],
    },
    {
      group: "Printing & Cards",
      items: [
        { id: "cardprinter",  icon: <PrinterIcon         fontSize="small" />, color: "#00695c", title: "ID Card Printer",               subtitle: "Pick the physical printer this facility uses so the OPD print job matches its layout", component: <CardPrinterSettings /> },
        { id: "insurance",    icon: <InsIcon             fontSize="small" />, color: "#1a6b8a", title: "Insurance Partners",            subtitle: "Manage partners displayed on the patient ID card (up to 10 active)",       component: <InsurancePartnerSettings /> },
      ],
    },
    {
      group: "Communications",
      items: [
        { id: "sms",          icon: <SmsOutlined                     fontSize="small" />, color: "#7b3f9e", title: "SMS Alert Settings",              subtitle: "Master on/off + per-event triggers",                            component: <SmsAlertSettings /> },
        { id: "smsgateway",   icon: <SmsOutlined                     fontSize="small" />, color: "#11998e", title: "SMS Gateway Configuration",       subtitle: "SMS Online GH provider — API key, sender ID, balance, test send", component: <SmsSettings /> },
        { id: "notifications",icon: <NotificationsActiveOutlined     fontSize="small" />, color: "#c0392b", title: "Notification & Alert Preferences", subtitle: "System notifications for administrators and authorised staff", component: <NotificationSettings /> },
      ],
    },
    {
      group: "Clinical & System",
      items: [
        { id: "compliance",   icon: <HourglassTopOutlined fontSize="small" />, color: "#b45309", title: "Admission Compliance",          subtitle: "Per-day vitals and nursing-note rules that gate patient discharge", component: <ComplianceSettings /> },
        { id: "autoupdate",   icon: <AutorenewOutlined    fontSize="small" />, color: "#2e8b57", title: "Auto-Update Settings",          subtitle: "Control system-wide automatic data refresh and sync behaviour",     component: <AutoUpdateSettings /> },
        { id: "general",      icon: <SettingsOutlined     fontSize="small" />, color: "#64748b", title: "Additional Settings",           subtitle: "Reserved for future configuration modules",                          component: <GeneralSettings /> },
      ],
    },
    {
      group: "Data Management",
      items: [
        { id: "backup",       icon: <BackupIcon           fontSize="small" />, color: "#0369a1", title: "Database Backup",               subtitle: "Download a full snapshot of the hospital database as a .sql.gz file", component: <DatabaseBackupSettings /> },
      ],
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", p: { xs: 2, md: 3 } }}>
        {/* Page header */}
        <Stack direction="row" alignItems="center" spacing={2} mb={1}>
          <Avatar sx={{
            bgcolor: alpha(SETTINGS_ACCENT, 0.18),
            color: SETTINGS_ACCENT,
            border: `1px solid ${alpha(SETTINGS_ACCENT, 0.30)}`,
            width: 44, height: 44,
          }}>
            <SettingsOutlined />
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, color: "text.primary", lineHeight: 1.15 }}>
              System Settings
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "text.secondary", mt: 0.25 }}>
              Administrative configuration — click any panel to expand its options.
            </Typography>
          </Box>
        </Stack>

        {/* Grouped, collapsible sections */}
        <Stack spacing={3} sx={{ mt: 3 }}>
          {SECTIONS.map((g) => (
            <Box key={g.group}>
              <Typography sx={{
                fontSize: "0.7rem", fontWeight: 800, letterSpacing: "1.2px",
                textTransform: "uppercase", color: "text.secondary",
                mb: 1, pl: 0.5,
              }}>
                {g.group}
              </Typography>
              <Stack spacing={1.25}>
                {g.items.map((s) => (
                  <SettingsAccordion
                    key={s.id}
                    icon={s.icon}
                    color={s.color}
                    title={s.title}
                    subtitle={s.subtitle}
                    defaultExpanded={s.defaultExpanded}
                  >
                    {s.component}
                  </SettingsAccordion>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </Box>
    </ThemeProvider>
  );
}